import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { organizationService, superAdminService } from '../services/api';

/**
 * Global organization filter (multi-org Slice C).
 *
 * A tenant admin (and super admin) can belong to / oversee multiple
 * organizations. This context holds the org currently selected in the header
 * switcher and exposes it to the Dashboard and every records list so they can
 * scope their data to one org (or "all").
 *
 * Non-admin users only ever see their own org server-side, so the switcher is
 * hidden for them and `orgId` stays 'all'.
 */

export interface OrgLite {
  organization_id: number;
  name: string;
}

interface OrgFilterValue {
  orgId: number | 'all';
  setOrgId: (id: number | 'all') => void;
  /** Convenience: the value to send as the `organization_id` query param. */
  orgParam: number | undefined;
  orgs: OrgLite[];
  canSwitch: boolean; // admin with more than one org
  loading: boolean;
}

const OrgFilterContext = createContext<OrgFilterValue>({
  orgId: 'all',
  setOrgId: () => {},
  orgParam: undefined,
  orgs: [],
  canSwitch: false,
  loading: false,
});

export const useOrgFilter = () => useContext(OrgFilterContext);

const STORAGE_KEY = 'org_filter_selected';

export const OrgFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const role = (user?.role || '').toLowerCase();
  const isAdmin = role === 'super_admin' || role === 'tenant_admin';

  // Organizations this user may actually act in, taken from the login payload.
  // Used to validate any persisted selection so a stale or forbidden choice can
  // never strand the user on an org they no longer have access to (e.g. after
  // their role or org membership was changed).
  const accessibleIds: number[] =
    Array.isArray(user?.organization_ids) && user.organization_ids.length
      ? user.organization_ids
      : user?.organization_id
      ? [user.organization_id]
      : [];

  const [orgs, setOrgs] = useState<OrgLite[]>([]);
  const [loading, setLoading] = useState<boolean>(isAdmin);
  const [orgId, setOrgIdState] = useState<number | 'all'>(() => {
    // Non-admins have no org switcher, so they must never be pinned to a single
    // org — always fall back to "all" (the API already limits their data to
    // their own organisations). This also discards any stale selection left
    // over from a previous session or a role/membership change.
    if (!isAdmin) return 'all';
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw || raw === 'all') return 'all';
    const n = Number(raw);
    if (!Number.isFinite(n)) return 'all';
    // Clamp to an org the user can access (revalidated again after fetch).
    if (accessibleIds.length && !accessibleIds.includes(n)) return 'all';
    return n;
  });

  // Drop any persisted selection we just clamped away so it cannot resurface on
  // the next mount and blank out the user's data.
  useEffect(() => {
    if (orgId === 'all') localStorage.removeItem(STORAGE_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOrgId = useCallback((id: number | 'all') => {
    setOrgIdState(id);
    if (id === 'all') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  // Re-evaluate when the signed-in user changes. This provider wraps the whole
  // app (including /login), so it first mounts BEFORE anyone is authenticated.
  // Login and logout happen via client-side navigation (no page reload), so
  // without this the provider would keep its initial "no user" state and the
  // org switcher would stay hidden until a manual browser refresh. Login/logout
  // dispatch an 'auth-changed' event; the 'storage' event covers other tabs.
  const [authTick, setAuthTick] = useState(0);
  useEffect(() => {
    const bump = () => setAuthTick((t) => t + 1);
    window.addEventListener('auth-changed', bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener('auth-changed', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setOrgs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const res =
          role === 'super_admin'
            ? await superAdminService.getAllOrganizations()
            : await organizationService.getAllOrganizations();
        if (cancelled) return;
        const list: OrgLite[] = (res.data || []).map((o: any) => ({
          organization_id: o.organization_id,
          name: o.name,
        })).sort((a: OrgLite, b: OrgLite) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setOrgs(list);
        // If the persisted selection is no longer valid, reset to "all" and
        // clear it so it cannot resurface on the next mount.
        setOrgIdState((prev) => {
          if (prev !== 'all' && !list.some((o) => o.organization_id === prev)) {
            localStorage.removeItem(STORAGE_KEY);
            return 'all';
          }
          return prev;
        });
      } catch {
        if (!cancelled) setOrgs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTick]);

  const canSwitch = isAdmin && orgs.length > 1;
  const orgParam = orgId === 'all' ? undefined : orgId;

  return (
    <OrgFilterContext.Provider value={{ orgId, setOrgId, orgParam, orgs, canSwitch, loading }}>
      {children}
    </OrgFilterContext.Provider>
  );
};

export default OrgFilterContext;
